/**
 * Impersonation session store (client state). Holds the impersonation JWT + the
 * target tenant while a super admin is "inside" a client account (AMLIQ-137).
 *
 * Deliberately SEPARATE from the auth store: the super admin's real identity
 * and admin token stay untouched in `auth.store` (localStorage) — the admin
 * token is the natural "backup" restored on exit. The axios interceptor reads
 * this store synchronously and, when a session is present, sends its token in
 * place of the admin token so the backend scopes every request to the tenant.
 *
 * Persisted to **sessionStorage** so impersonation is a per-tab mode: it
 * survives a reload within the same tab but not a new tab / closing the tab.
 */
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import type { AuthUser, ImpersonationMode, ImpersonationTenant } from '../types'
import { STORAGE_KEYS } from '@/config/app.config'

/**
 * The super admin's OWN authorization identity, snapshotted before
 * `buildImpersonatedUser` overwrites `auth.store.user` with the account admin.
 *
 * Without this the real role is simply gone for the duration of the session:
 * the impersonated user carries `ADMIN_USER` + `COMPLIANCE_OFFICER` and no
 * explicit grants, so `hasRole(user, SYSTEM_ADMIN)` is false while the backend
 * still treats the caller as a super admin (see the "Manual SMS (Super Admin)"
 * endpoints, which explicitly allow an impersonating super admin). Features
 * gated on *who is really logged in* — as opposed to *what the current scope
 * may do* — resolve against this. See `useIsSuperAdmin`.
 */
export type RealIdentity = Pick<AuthUser, 'id' | 'role' | 'roles' | 'permissions'>

export interface ImpersonationSession {
  /**
   * Impersonation JWT, used in place of the admin token. The JWT carries no
   * `exp`, but its server-side session lapses after 60 minutes of inactivity
   * (each request extends the window) — see `impersonation.api.ts`.
   */
  token: string
  tenant: ImpersonationTenant
  /** Granted access modes (union of these roles' permissions). */
  mode: Array<ImpersonationMode>
  /**
   * Who actually started the session (see {@link RealIdentity}). `null` only if
   * the profile was somehow missing at start — consumers then fall back to the
   * (impersonated) auth-store user, i.e. they fail closed.
   */
  realIdentity: RealIdentity | null
}

interface ImpersonationState {
  session: ImpersonationSession | null
  /** Begin impersonating (after `POST /admin/auth/impersonate` succeeds). */
  start: (session: ImpersonationSession) => void
  /** Return to the platform view (after exit, or on token failure). */
  stop: () => void
}

export const useImpersonationStore = create<ImpersonationState>()(
  persist(
    (set) => ({
      session: null,
      start: (session) => set({ session }),
      stop: () => set({ session: null }),
    }),
    {
      name: STORAGE_KEYS.impersonation,
      storage: createJSONStorage(() => sessionStorage),
      version: 2,
      // Only the session is state; `start`/`stop` are actions. Naming it
      // explicitly also fixes the persisted shape that `migrate` must return.
      partialize: (state) => ({ session: state.session }),
      // v1 sessions predate `realIdentity`, so a rehydrated one would report
      // its owner as "not a super admin" and silently hide super-admin-only
      // actions. There is nothing to backfill from (the real role was already
      // overwritten in auth.store), so drop the session: the tab returns to the
      // platform view and the super admin re-enters the account.
      migrate: () => ({ session: null }),
    },
  ),
)

/** Non-reactive accessor for use outside React (axios interceptor). */
export const impersonationStore = {
  getState: useImpersonationStore.getState,
}
